// DEPLOY: /var/www/woody.wtf/frontend/src/chart/Toolbar.js
// woody.wtf chart toolbar. Driven by a plain list of groups (see GROUPS in Chart2.js):
//   { id, label, icon, soon?, tools: [{ id, label, icon }] }
// Adding a tool to any group = one entry in that list. A group's button shows the icon
// of the tool last used from it.
//   Desktop: vertical icon rail; clicking a group opens a popout beside it.
//   Phone:   bottom bar; tapping a group slides up a sheet with large tap targets.
// Groups marked `soon` are placeholders for later phases (shown greyed, not clickable).
import React, { useState, useEffect, useRef } from 'react';

const S = {
  rail:      { width: '44px', flexShrink: 0, background: 'var(--surface)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '6px 0', position: 'relative', zIndex: 20 },
  railBtn:   { width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: 'var(--radius)', background: 'none', color: 'var(--text-muted)', cursor: 'pointer', position: 'relative' },
  caret:     { position: 'absolute', right: '2px', bottom: '2px', width: 0, height: 0, borderLeft: '4px solid transparent', borderBottom: '4px solid currentColor', opacity: 0.6 },
  popout:    { position: 'absolute', left: '46px', minWidth: '180px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', padding: '4px', zIndex: 30 },
  popTitle:  { fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 8px 4px' },
  popItem:   { display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '7px 8px', border: 'none', background: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', color: 'var(--text)', textAlign: 'left', fontFamily: "'Space Grotesk',sans-serif" },
  bar:       { height: '50px', flexShrink: 0, background: 'var(--surface)', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'stretch', justifyContent: 'space-around' },
  barBtn:    { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', border: 'none', background: 'none', color: 'var(--text-muted)', fontSize: '10px', fontFamily: "'JetBrains Mono',monospace", cursor: 'pointer' },
  backdrop:  { position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 40 },
  sheet:     { position: 'fixed', left: 0, right: 0, bottom: 0, background: 'var(--surface)', borderRadius: '14px 14px 0 0', boxShadow: '0 -4px 24px rgba(0,0,0,0.15)', padding: '8px 12px 20px', zIndex: 41 },
  handle:    { width: '36px', height: '4px', borderRadius: '2px', background: 'var(--border)', margin: '2px auto 8px' },
  sheetGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: '8px' },
  sheetItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '14px 4px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontSize: '12px', cursor: 'pointer', fontFamily: "'Space Grotesk',sans-serif" },
};

export default function Toolbar({ groups, mobile, onTool, active }) {   // active = group with a tool in use
  const [open, setOpen]   = useState(null);   // id of the group whose popout/sheet is open
  const [last, setLast]   = useState({});     // groupId -> last tool used from it
  const [popTop, setPopTop] = useState(0);
  const railRef = useRef(null);

  // Desktop: click anywhere outside the rail closes the popout.
  useEffect(() => {
    if (!open || mobile) return;
    const close = e => { if (!railRef.current?.contains(e.target)) setOpen(null); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open, mobile]);

  const pick = (g, t) => { setLast(l => ({ ...l, [g.id]: t })); setOpen(null); onTool(t.id); };
  const iconOf = g => (last[g.id] || g).icon;

  const toggle = (g, e) => {
    if (g.soon) return;
    if (!mobile) setPopTop(e.currentTarget.offsetTop);
    setOpen(o => (o === g.id ? null : g.id));
  };

  const openGroup = groups.find(g => g.id === open);

  if (mobile) return (
    <>
      <div style={S.bar}>
        {groups.map(g => {
          const Icon = iconOf(g);
          return (
            <button key={g.id} style={{ ...S.barBtn, opacity: g.soon ? 0.35 : 1, color: open === g.id || active === g.id ? 'var(--accent)' : S.barBtn.color }}
                    onClick={e => toggle(g, e)} aria-label={g.soon ? `${g.label} (coming soon)` : g.label}>
              <Icon size={20} strokeWidth={1.8} />
              <span>{g.label}</span>
            </button>
          );
        })}
      </div>
      {openGroup && (
        <>
          <div style={S.backdrop} onClick={() => setOpen(null)} />
          <div style={S.sheet}>
            <div style={S.handle} />
            <div style={S.popTitle}>{openGroup.label}</div>
            <div style={S.sheetGrid}>
              {openGroup.tools.map(t => (
                <button key={t.id} style={S.sheetItem} onClick={() => pick(openGroup, t)}>
                  <t.icon size={22} strokeWidth={1.8} />{t.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );

  return (
    <div ref={railRef} style={S.rail}>
      {groups.map(g => {
        const Icon = iconOf(g);
        return (
          <button key={g.id} title={g.soon ? `${g.label} (coming soon)` : g.label}
                  style={{ ...S.railBtn, opacity: g.soon ? 0.35 : 1, cursor: g.soon ? 'default' : 'pointer',
                           background: open === g.id || active === g.id ? 'var(--accent-dim)' : 'none', color: open === g.id || active === g.id ? 'var(--accent)' : S.railBtn.color }}
                  onClick={e => toggle(g, e)}>
            <Icon size={18} strokeWidth={1.8} />
            {!g.soon && <span style={S.caret} />}
          </button>
        );
      })}
      {openGroup && (
        <div style={{ ...S.popout, top: popTop }}>
          <div style={S.popTitle}>{openGroup.label}</div>
          {openGroup.tools.map(t => (
            <button key={t.id} style={S.popItem} onClick={() => pick(openGroup, t)}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-faint)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <t.icon size={16} strokeWidth={1.8} />{t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
